import { Head, Link, router, useForm } from "@inertiajs/react"
import { CircleAlert, FileSpreadsheet, Pencil } from "lucide-react"
import { useMemo, useState } from "react"

import InputError from "@/components/input-error"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import AppLayout from "@/layouts/app-layout"
import { formatCurrency } from "@/lib/utils"
import {
  businessProfilePath,
  entriesPath,
  newEntryPath,
  scheduleCCategoryMappingsPath,
  scheduleCPath,
} from "@/routes"
import type { BreadcrumbItem } from "@/types"

interface BusinessProfile {
  name: string
  persisted: boolean
}

interface TaxonomyLine {
  key: string
  label: string
  line: string
}

interface CategoryTotal {
  id: number
  name: string
  total_cents: number
}

interface PartIILine extends TaxonomyLine {
  total_cents: number
  categories: CategoryTotal[]
}

interface ScheduleCProps {
  year: number | null
  available: boolean
  unavailable_reason?: "future_year" | "invalid_year" | "taxonomy_unavailable"
  available_years: number[]
  business_profile: BusinessProfile
  taxonomy?: { tax_year: number; part_ii_lines: TaxonomyLine[] }
  gross_receipts_cents?: number
  part_ii_lines?: PartIILine[]
  included_ordinary_expenses_cents?: number
  unmapped_categories?: CategoryTotal[]
  uncategorized_expenses?: { total_cents: number; entry_count: number }
  net_profit_or_loss_cents?: number
  excluded_sections?: string[]
}

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Schedule C Worksheet", href: scheduleCPath() },
]

function formatCents(cents: number) {
  return formatCurrency(cents / 100)
}

function dateRangeFor(year: number) {
  return { start_date: `${year}-01-01`, end_date: `${year}-12-31` }
}

function unavailableMessage(reason?: ScheduleCProps["unavailable_reason"]) {
  switch (reason) {
    case "future_year":
      return "Choose the current year or a completed year. Future-year worksheets are not available."
    case "invalid_year":
      return "Enter a four-digit tax year to review its worksheet."
    default:
      return "This tax year's Schedule C line taxonomy has not been reviewed yet, so no worksheet totals are shown."
  }
}

export default function ScheduleCWorksheet({
  year,
  available,
  available_years,
  business_profile,
  taxonomy,
  gross_receipts_cents = 0,
  part_ii_lines = [],
  included_ordinary_expenses_cents = 0,
  unmapped_categories = [],
  uncategorized_expenses = { total_cents: 0, entry_count: 0 },
  net_profit_or_loss_cents = 0,
  excluded_sections = [],
  unavailable_reason,
}: ScheduleCProps) {
  const [yearInput, setYearInput] = useState(
    String(year ?? new Date().getFullYear()),
  )
  const [mappingDialogOpen, setMappingDialogOpen] = useState(false)
  const [mappingCategory, setMappingCategory] = useState<CategoryTotal | null>(
    null,
  )
  const [businessFeedback, setBusinessFeedback] = useState("")
  const [mappingFeedback, setMappingFeedback] = useState("")

  const businessForm = useForm({ name: business_profile.name })
  const mappingForm = useForm({
    category_id: 0,
    tax_year: year ?? new Date().getFullYear(),
    line_key: "",
  })

  const yearLinks = useMemo(
    () =>
      Array.from(
        new Set<number>([
          new Date().getFullYear(),
          ...available_years,
          ...(year ? [year] : []),
        ]),
      ).sort((a, b) => b - a),
    [available_years, year],
  )

  const isEmpty =
    gross_receipts_cents === 0 &&
    included_ordinary_expenses_cents === 0 &&
    unmapped_categories.length === 0 &&
    uncategorized_expenses.entry_count === 0

  function selectYear(nextYear: string) {
    setYearInput(nextYear)
    router.get(scheduleCPath(), { year: nextYear })
  }

  function submitYear(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    selectYear(yearInput)
  }

  function submitBusinessName(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setBusinessFeedback("")
    businessForm.transform((data) => ({
      ...data,
      year: year ?? new Date().getFullYear(),
    }))
    businessForm.patch(businessProfilePath(), {
      preserveScroll: true,
      onSuccess: () => setBusinessFeedback("Business name saved."),
      onError: () =>
        setBusinessFeedback(
          "We couldn't save the business name. Review the field and try again.",
        ),
    })
  }

  function openMappingDialog(category: CategoryTotal) {
    if (!year || !taxonomy) return

    setMappingCategory(category)
    setMappingFeedback("")
    mappingForm.clearErrors()
    mappingForm.setData({
      category_id: category.id,
      tax_year: year,
      line_key: taxonomy.part_ii_lines[0]?.key ?? "",
    })
    setMappingDialogOpen(true)
  }

  function submitMapping(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setMappingFeedback("")
    mappingForm.post(scheduleCCategoryMappingsPath(), {
      preserveScroll: true,
      onSuccess: () => {
        setMappingDialogOpen(false)
        setMappingFeedback(
          "Category mapping saved. Worksheet totals have been refreshed.",
        )
      },
      onError: () =>
        setMappingFeedback(
          "We couldn't save this mapping. Review the selection and try again.",
        ),
    })
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Schedule C Worksheet" />

      <div className="mx-auto w-full max-w-5xl space-y-6 p-4">
        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-start">
          <div>
            <h1 className="text-xl font-semibold">Schedule C Worksheet</h1>
            <p className="text-muted-foreground text-sm">
              Tax-preparation support for reviewing one business. Verify these
              totals against the official instructions before filing.
            </p>
          </div>

          <form onSubmit={submitYear} className="flex items-end gap-2">
            <div className="grid gap-1">
              <Label htmlFor="schedule-c-tax-year" className="text-xs">
                Tax year
              </Label>
              <Input
                id="schedule-c-tax-year"
                type="number"
                inputMode="numeric"
                min="1900"
                max={new Date().getFullYear()}
                value={yearInput}
                onChange={(event) => setYearInput(event.target.value)}
                className="w-24"
              />
            </div>
            <Button type="submit" size="sm">
              View
            </Button>
          </form>
        </div>

        <div
          className="flex flex-wrap gap-2"
          aria-label="Quick tax year selection"
        >
          {yearLinks.map((availableYear) => (
            <Button
              key={availableYear}
              type="button"
              size="sm"
              variant={year === availableYear ? "default" : "outline"}
              onClick={() => selectYear(String(availableYear))}
            >
              {availableYear}
              {available_years.includes(availableYear)
                ? ""
                : " (review needed)"}
            </Button>
          ))}
        </div>

        {!available ? (
          <Card>
            <CardContent className="py-10 text-center">
              <CircleAlert className="text-muted-foreground mx-auto mb-4 h-10 w-10" />
              <h2 className="text-lg font-medium">
                {year ? `${year} worksheet needs review` : "Tax year needed"}
              </h2>
              <p className="text-muted-foreground mx-auto mt-2 max-w-lg text-sm">
                {unavailableMessage(unavailable_reason)}
              </p>
              {available_years.length > 0 && (
                <p className="text-muted-foreground mt-4 text-sm">
                  Reviewed worksheet year
                  {available_years.length === 1 ? "" : "s"}:{" "}
                  {available_years.join(", ")}
                </p>
              )}
            </CardContent>
          </Card>
        ) : (
          <>
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Business context</CardTitle>
              </CardHeader>
              <CardContent>
                <form
                  onSubmit={submitBusinessName}
                  className="flex flex-col gap-3 sm:flex-row sm:items-end"
                >
                  <div className="grid flex-1 gap-2">
                    <Label htmlFor="business-name">Business name</Label>
                    <Input
                      id="business-name"
                      value={businessForm.data.name}
                      onChange={(event) =>
                        businessForm.setData("name", event.target.value)
                      }
                      aria-describedby="business-name-help business-name-error"
                    />
                    <p
                      id="business-name-help"
                      className="text-muted-foreground text-xs"
                    >
                      {business_profile.persisted
                        ? "Shown with this worksheet and kept separate from the selected year's totals."
                        : "Save a name to create your business profile."}
                    </p>
                    <InputError
                      id="business-name-error"
                      messages={businessForm.errors.name}
                    />
                  </div>
                  <Button type="submit" disabled={businessForm.processing}>
                    <Pencil className="mr-2 h-4 w-4" />
                    {businessForm.processing ? "Saving…" : "Save name"}
                  </Button>
                </form>
                <p className="sr-only" role="status" aria-live="polite">
                  {businessFeedback}
                </p>
                {businessFeedback && (
                  <p className="mt-3 text-sm" role="status" aria-live="polite">
                    {businessFeedback}
                  </p>
                )}
              </CardContent>
            </Card>

            <div className="grid gap-4 sm:grid-cols-3">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">
                    Part I gross receipts
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="font-mono text-2xl font-bold">
                    {formatCents(gross_receipts_cents)}
                  </p>
                  <p className="text-muted-foreground mt-1 text-xs">
                    Revenue recorded during {year}
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">
                    Included ordinary expenses
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="font-mono text-2xl font-bold">
                    {formatCents(included_ordinary_expenses_cents)}
                  </p>
                  <p className="text-muted-foreground mt-1 text-xs">
                    Mapped Part II categories only
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">
                    Net profit or loss
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="font-mono text-2xl font-bold">
                    {formatCents(net_profit_or_loss_cents)}
                  </p>
                  <p className="text-muted-foreground mt-1 text-xs">
                    Before specialized Schedule C sections
                  </p>
                </CardContent>
              </Card>
            </div>

            {isEmpty && (
              <Card>
                <CardContent className="py-8 text-center">
                  <FileSpreadsheet className="text-muted-foreground mx-auto mb-3 h-10 w-10" />
                  <h2 className="font-medium">
                    No eligible entries for {year}
                  </h2>
                  <p className="text-muted-foreground mx-auto mt-2 max-w-lg text-sm">
                    Add or import revenue and expense entries to start the
                    worksheet. Transfers and tax payments are excluded from
                    Schedule C totals.
                  </p>
                  <Button className="mt-4" size="sm" asChild>
                    <Link href={newEntryPath()}>Add an entry</Link>
                  </Button>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  Part II ordinary expenses
                </CardTitle>
                <p className="text-muted-foreground text-sm">
                  Mapped categories are grouped by the {taxonomy?.tax_year}{" "}
                  Schedule C line taxonomy.
                </p>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableCaption className="sr-only">
                    Part II ordinary expense totals for {year}
                  </TableCaption>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Line</TableHead>
                      <TableHead>Expense category</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {part_ii_lines.map((line) => (
                      <TableRow key={line.key}>
                        <TableCell className="font-mono text-xs">
                          {line.line}
                        </TableCell>
                        <TableCell>
                          <p className="font-medium">{line.label}</p>
                          {line.categories.length > 0 ? (
                            <ul className="text-muted-foreground mt-1 space-y-1 text-xs">
                              {line.categories.map((category) => (
                                <li key={category.id}>
                                  <Link
                                    className="underline underline-offset-2"
                                    href={entriesPath({
                                      ...dateRangeFor(year!),
                                      direction: "expense",
                                      category_id: category.id,
                                    })}
                                  >
                                    {category.name} (
                                    {formatCents(category.total_cents)})
                                  </Link>
                                  <Button
                                    variant="link"
                                    size="sm"
                                    className="h-auto px-1 py-0 text-xs"
                                    onClick={() => openMappingDialog(category)}
                                  >
                                    Change line
                                  </Button>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p className="text-muted-foreground mt-1 text-xs">
                              No mapped entries
                            </p>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-mono font-medium">
                          {formatCents(line.total_cents)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                  <TableFooter>
                    <TableRow>
                      <TableCell colSpan={2}>
                        Total included ordinary expenses
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {formatCents(included_ordinary_expenses_cents)}
                      </TableCell>
                    </TableRow>
                  </TableFooter>
                </Table>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  Review before including
                </CardTitle>
                <p className="text-muted-foreground text-sm">
                  Unmapped and uncategorized expenses are not included in the
                  ordinary-expense total.
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                {unmapped_categories.length > 0 ? (
                  <div className="space-y-2">
                    <h3 className="text-sm font-medium">Unmapped categories</h3>
                    {unmapped_categories.map((category) => (
                      <div
                        key={category.id}
                        className="flex flex-col gap-2 rounded-md border p-3 sm:flex-row sm:items-center sm:justify-between"
                      >
                        <div>
                          <p className="font-medium">{category.name}</p>
                          <p className="text-muted-foreground text-sm">
                            {formatCents(category.total_cents)} excluded until
                            mapped
                          </p>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => openMappingDialog(category)}
                        >
                          Choose Schedule C line
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-sm">
                    All categorized expense amounts for this year have a
                    Schedule C line mapping.
                  </p>
                )}

                {uncategorized_expenses.entry_count > 0 && (
                  <Alert>
                    <CircleAlert />
                    <AlertTitle>Uncategorized expenses need review</AlertTitle>
                    <AlertDescription>
                      {uncategorized_expenses.entry_count}{" "}
                      {uncategorized_expenses.entry_count === 1
                        ? "entry totals"
                        : "entries total"}{" "}
                      {formatCents(uncategorized_expenses.total_cents)} and
                      remain excluded.
                      <Button
                        variant="link"
                        className="h-auto px-1 py-0"
                        asChild
                      >
                        <Link
                          href={entriesPath({
                            ...dateRangeFor(year!),
                            direction: "expense",
                            status: "needs_review",
                            schedule_c_year: year!,
                          })}
                        >
                          Categorize these expenses
                        </Link>
                      </Button>
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>

            <Alert>
              <CircleAlert />
              <AlertTitle>Not a filing-ready return</AlertTitle>
              <AlertDescription>
                This worksheet does not calculate{" "}
                {excluded_sections.join(", ").toLowerCase()}. Transfers and
                tax-payment entries are excluded from all worksheet totals.
              </AlertDescription>
            </Alert>
          </>
        )}
      </div>

      <Dialog open={mappingDialogOpen} onOpenChange={setMappingDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Map {mappingCategory?.name}</DialogTitle>
            <DialogDescription>
              Choose the ordinary-expense line for this category in {year}. The
              amount remains excluded until you save this mapping.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitMapping} className="space-y-4">
            <div className="grid gap-2">
              <Label htmlFor="schedule-c-line">Schedule C line</Label>
              <Select
                value={mappingForm.data.line_key}
                onValueChange={(value) =>
                  mappingForm.setData("line_key", value)
                }
              >
                <SelectTrigger
                  id="schedule-c-line"
                  aria-label="Schedule C line"
                >
                  <SelectValue placeholder="Choose a line" />
                </SelectTrigger>
                <SelectContent>
                  {taxonomy?.part_ii_lines.map((line) => (
                    <SelectItem key={line.key} value={line.key}>
                      Line {line.line}: {line.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <InputError messages={mappingForm.errors.line_key} />
            </div>
            <InputError messages={mappingForm.errors.category_id} />
            {mappingFeedback && (
              <p className="text-sm" role="status" aria-live="polite">
                {mappingFeedback}
              </p>
            )}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setMappingDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={mappingForm.processing || !mappingForm.data.line_key}
              >
                {mappingForm.processing ? "Saving…" : "Save mapping"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <p className="sr-only" role="status" aria-live="polite">
        {mappingFeedback}
      </p>
    </AppLayout>
  )
}
